/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * A tab left open across a deploy.
 *
 * Every screen is its own file, named for its contents, and a deploy replaces
 * them all. A page loaded before the deploy still asks for the old names, the
 * server no longer has them, and the next screen opened fails with "Failed to
 * fetch dynamically imported module". Nothing is wrong with the person's
 * access: the page in front of them is simply older than the server.
 *
 * The answer is to load the new version, once. Once, because a file that is
 * missing for any other reason would otherwise reload for ever; a second
 * failure within a minute is shown as an ordinary error instead.
 */

const KEY = 'vellum:reloaded-for-new-version';
const WITHIN_MS = 60_000;

/** Whether this error is a screen's file that is no longer on the server. */
export function isNewVersionError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
    message,
  );
}

/**
 * Load the new version, unless this tab already did in the last minute.
 * Returns whether it is reloading. Storage that throws (a private window, a
 * blocked site) still allows the one reload, and simply cannot remember it.
 */
export function reloadForNewVersion(
  win: Window = window,
  now: number = Date.now(),
): boolean {
  const last = lastReload(win);
  if (now - last < WITHIN_MS) return false;
  try {
    win.sessionStorage.setItem(KEY, String(now));
  } catch {
    // Nothing to remember it in; the reload still happens once.
  }
  win.location.reload();
  return true;
}

function lastReload(win: Window): number {
  try {
    return Number(win.sessionStorage.getItem(KEY) ?? 0);
  } catch {
    return 0;
  }
}

/**
 * Vite says so itself when a screen's file cannot be fetched: `vite:preloadError`
 * on the window. Answered by loading the new version, before the router ever
 * shows an error.
 */
export function watchForNewVersion(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewVersion(win)) event.preventDefault();
  });
}
